import { setupWorker, rest } from 'msw';
import media from './data/feeds/media.json';
import { replaceRemoteImageSrc } from './feed-handlers.ts';
import mockFavorites from './data/feeds/favorites.ts';
import mockContinueWatching from './data/feeds/continue-watching.json';
import mockEpg from './data/feeds/epg-client.json';

type MockPlaylistRecord = {
  id: string;
  title: string;
  summary: string;
};

type ParsedCollectionsRequest = {
  kind: 'selector' | 'editable';
  itemId: string;
  collectionId?: string;
};

const baseMockPlaylists: MockPlaylistRecord[] = [
  {
    id: 'playlist-favorites',
    title: 'Favorites',
    summary: '12 items',
  },
  {
    id: 'playlist-workout',
    title: 'Workout Mix',
    summary: '7 items',
  },
];

const selectedPlaylistIdsByItemId = new Map<string, Set<string>>();
const createdPlaylistIdsByItemId = new Map<string, string[]>();
const createdPlaylistTitlesById = new Map<string, string>();
const renamedPlaylistTitlesById = new Map<string, string>();
const deletedPlaylistIdsByItemId = new Map<string, Set<string>>();

function parseCollectionsRequest(
  feedUrl: string
): ParsedCollectionsRequest | null {
  let parsedUrl: URL;

  try {
    parsedUrl = feedUrl.startsWith('http')
      ? new URL(feedUrl)
      : new URL(feedUrl, 'https://mock.local');
  } catch {
    return null;
  }

  const path = parsedUrl.pathname;
  const itemId = parsedUrl.searchParams.get('item_id') || 'mock-item';
  const editable = parsedUrl.searchParams.get('editable') === 'true';

  if (path === '/user/collections' && !editable) {
    return {
      kind: 'selector',
      itemId,
    };
  }

  const editableCollectionMatch = path.match(/^\/user\/collections\/([^/]+)$/);

  if (editableCollectionMatch && editable) {
    return {
      kind: 'editable',
      itemId,
      collectionId: decodeURIComponent(editableCollectionMatch[1]),
    };
  }

  return null;
}

function getDeletedPlaylistIdSet(itemId: string): Set<string> {
  const existing = deletedPlaylistIdsByItemId.get(itemId);

  if (existing) {
    return existing;
  }

  const next = new Set<string>();
  deletedPlaylistIdsByItemId.set(itemId, next);

  return next;
}

function resolvePlaylistTitle(playlist: MockPlaylistRecord): string {
  return (
    renamedPlaylistTitlesById.get(playlist.id) ||
    createdPlaylistTitlesById.get(playlist.id) ||
    playlist.title
  );
}

function getSelectedPlaylistIdsForItem(itemId: string): Set<string> {
  const existing = selectedPlaylistIdsByItemId.get(itemId);

  if (existing) {
    return existing;
  }

  const fallbackSelection = new Set<string>(['playlist-favorites']);
  selectedPlaylistIdsByItemId.set(itemId, fallbackSelection);

  return fallbackSelection;
}

function getPlaylistsForItem(itemId: string): MockPlaylistRecord[] {
  const deletedIds = getDeletedPlaylistIdSet(itemId);
  const createdIds = createdPlaylistIdsByItemId.get(itemId) || [];
  const createdPlaylists: MockPlaylistRecord[] = createdIds.map(
    (createdId, createdIndex) => {
      const createdTitle =
        resolvePlaylistTitle({
          id: createdId,
          title: `New Playlist ${createdIndex + 1}`,
          summary: '0 items',
        }) || `New Playlist ${createdIndex + 1}`;

      return {
        id: createdId,
        title: createdTitle,
        summary: '0 items',
      };
    }
  );

  return [...baseMockPlaylists, ...createdPlaylists]
    .filter((playlist) => !deletedIds.has(playlist.id))
    .map((playlist) => ({
      ...playlist,
      title: resolvePlaylistTitle(playlist),
    }));
}

function buildEditableCollectionFeed({
  itemId,
  collectionId,
}: {
  itemId: string;
  collectionId: string;
}) {
  const playlists = getPlaylistsForItem(itemId);
  const collection = playlists.find((playlist) => playlist.id === collectionId);
  const collectionTitle = collection?.title || 'Playlist';

  return {
    role: 'dynamic_collection',
    dynamic_collection_options: {
      operations: 'remove,reorder',
    },
    title: collectionTitle,
    entry: [
      {
        id: `${collectionId}-edit-name`,
        title: 'Edit Playlist Name',
        extensions: {
          entry_action: [
            {
              label: 'Edit Playlist Name',
              button: {
                alias: 'edit_name_and_details',
              },
              dismiss_on_action: false,
              actions: [
                {
                  type: 'editCollectionName',
                  options: {
                    collectionId,
                    name: collectionTitle,
                  },
                },
              ],
            },
          ],
        },
      },
      {
        id: `${collectionId}-delete`,
        title: 'Delete Playlist',
        extensions: {
          entry_action: [
            {
              label: 'Delete Playlist',
              button: {
                alias: 'delete_collection',
              },
              dismiss_on_action: false,
              actions: [
                {
                  type: 'confirmDialog',
                  options: {
                    title: 'Delete Playlist?',
                    message: `Are you sure you want to delete ${collectionTitle}? This action can't be undone.`,
                    okButtonText: 'Delete',
                    cancelButtonText: 'Cancel',
                  },
                },
                {
                  type: 'sendCloudEvent',
                  options: {
                    url: '/mock/playlists/mutation',
                    type: 'com.applicaster.collection.delete.v1',
                    subject: 'collection_mutation',
                    data: {
                      itemId,
                      collectionId,
                    },
                  },
                },
                {
                  type: 'refreshComponent',
                },
              ],
            },
          ],
        },
      },
      {
        id: `${collectionId}-track-1`,
        title: 'Mock Track 1',
        tap_actions: {
          actions: [
            {
              type: 'sendCloudEvent',
              options: {
                url: '/mock/playlists/mutation',
                type: 'com.applicaster.collection.remove.v1',
                subject: 'collection_mutation',
                data: {
                  itemId,
                  collectionId,
                  trackId: `${collectionId}-track-1`,
                },
              },
            },
            {
              type: 'refreshComponent',
            },
          ],
        },
      },
    ],
  };
}

function buildSelectorFeed(itemId: string) {
  const selectedIds = getSelectedPlaylistIdsForItem(itemId);
  const playlists = getPlaylistsForItem(itemId);

  return {
    role: 'collection_selector',
    behavior: {
      select_mode: 'multi',
      current_selection: Array.from(selectedIds),
    },
    title: 'Add to Playlist',
    subtitle: 'Select one or more playlists',
    section_title: 'Playlists',
    extensions: {
      role: 'collection_selector',
      behavior: {
        select_mode: 'multi',
        current_selection: Array.from(selectedIds),
      },
      dynamic_collection_options: {
        postUrl: '/mock/playlists/mutation',
        operations: 'add',
        events: {
          add: [
            {
              type: 'showTextInput',
              options: {
                headerTitle: 'Create New Playlist',
                inputLabel: 'Name your playlist',
                defaultValue: '',
                buttonLabel: 'Create',
                maxLength: 80,
                actions: [
                  {
                    type: 'sendCloudEvent',
                    options: {
                      url: '/mock/playlists/mutation',
                      type: 'com.applicaster.collection.create.v1',
                      subject: 'create_collection',
                      data: {
                        itemId,
                      },
                    },
                  },
                  {
                    type: 'showToast',
                    options: {
                      message: 'Playlist created.',
                    },
                  },
                  {
                    type: 'refreshComponent',
                  },
                ],
              },
            },
          ],
        },
      },
    },
    entry: playlists.map((playlist) => ({
      id: playlist.id,
      title: playlist.title,
      summary: playlist.summary,
      current_selection: selectedIds.has(playlist.id),
      tap_actions: {
        actions: [
          {
            type: 'sendCloudEvent',
            options: {
              url: '/mock/playlists/mutation',
              type: selectedIds.has(playlist.id)
                ? 'com.applicaster.collection.remove.v1'
                : 'com.applicaster.collection.add.v1',
              subject: 'collection_mutation',
              data: {
                itemId,
                collectionId: playlist.id,
              },
            },
          },
        ],
      },
    })),
  };
}

function getItemIdsByCollectionId(collectionId: string): string[] {
  const itemIds = new Set<string>();

  for (const [itemId, selectedIds] of selectedPlaylistIdsByItemId.entries()) {
    if (selectedIds.has(collectionId)) {
      itemIds.add(itemId);
    }
  }

  for (const [itemId, createdIds] of createdPlaylistIdsByItemId.entries()) {
    if (createdIds.includes(collectionId)) {
      itemIds.add(itemId);
    }
  }

  return Array.from(itemIds);
}

function applyMockPlaylistMutation(eventBody: unknown): void {
  const parsedBody =
    eventBody && typeof eventBody === 'object'
      ? (eventBody as Record<string, unknown>)
      : undefined;

  const eventType =
    typeof parsedBody?.type === 'string' ? parsedBody.type : undefined;
  const eventData =
    parsedBody?.data && typeof parsedBody.data === 'object'
      ? (parsedBody.data as Record<string, unknown>)
      : undefined;

  const itemId = typeof eventData?.itemId === 'string' ? eventData.itemId : '';

  if (
    eventType === 'com.applicaster.collection.toggle.v1' ||
    eventType === 'com.applicaster.collection.add.v1' ||
    eventType === 'com.applicaster.collection.remove.v1'
  ) {
    if (!itemId) {
      return;
    }

    const collectionId =
      typeof eventData?.collectionId === 'string' ? eventData.collectionId : '';

    if (!collectionId) {
      return;
    }

    const selectedIds = getSelectedPlaylistIdsForItem(itemId);

    if (eventType === 'com.applicaster.collection.add.v1') {
      selectedIds.add(collectionId);
      return;
    }

    if (eventType === 'com.applicaster.collection.remove.v1') {
      selectedIds.delete(collectionId);
      return;
    }

    if (selectedIds.has(collectionId)) {
      selectedIds.delete(collectionId);
      return;
    }

    selectedIds.add(collectionId);
    return;
  }

  if (eventType === 'com.applicaster.collection.create.v1') {
    if (!itemId) {
      return;
    }

    const createdIds = createdPlaylistIdsByItemId.get(itemId) || [];
    const nextIndex = createdIds.length + 1;
    const createdId = `playlist-created-${itemId}-${nextIndex}`;
    const requestedName =
      typeof eventData?.name === 'string' ? eventData.name.trim() : '';
    const createdTitle = requestedName || `New Playlist ${nextIndex}`;

    createdPlaylistIdsByItemId.set(itemId, [...createdIds, createdId]);
    createdPlaylistTitlesById.set(createdId, createdTitle);

    const selectedIds = getSelectedPlaylistIdsForItem(itemId);
    selectedIds.add(createdId);

    return;
  }

  if (eventType === 'com.applicaster.collection.rename.v1') {
    const collectionId =
      typeof eventData?.collectionId === 'string' ? eventData.collectionId : '';
    const nextName =
      typeof eventData?.name === 'string' ? eventData.name.trim() : '';

    if (!collectionId || !nextName) {
      return;
    }

    renamedPlaylistTitlesById.set(collectionId, nextName);

    return;
  }

  if (eventType === 'com.applicaster.collection.delete.v1') {
    const collectionId =
      typeof eventData?.collectionId === 'string' ? eventData.collectionId : '';

    if (!collectionId) {
      return;
    }

    const relatedItemIds = itemId
      ? [itemId]
      : getItemIdsByCollectionId(collectionId);

    for (const relatedItemId of relatedItemIds) {
      const deletedIds = getDeletedPlaylistIdSet(relatedItemId);
      deletedIds.add(collectionId);

      const selectedIds = getSelectedPlaylistIdsForItem(relatedItemId);
      selectedIds.delete(collectionId);

      const createdIds = createdPlaylistIdsByItemId.get(relatedItemId) || [];
      createdPlaylistIdsByItemId.set(
        relatedItemId,
        createdIds.filter((createdId) => createdId !== collectionId)
      );
    }

    return;
  }

  if (eventType === 'com.applicaster.collection.remove.v1') {
    if (!itemId) {
      return;
    }

    return;
  }
}

const worker = setupWorker(
  rest.get(
    'https://pipes2-server-example.herokuapp.com/media',
    (_, res, ctx) => {
      return res(ctx.json(replaceRemoteImageSrc(media)));
    }
  ),
  rest.post('*/api/is-logged-in', (_, res, ctx) => {
    return res(ctx.json({ isLoggedIn: true }));
  }),
  rest.post('*/api/favorite-action', async (req, res, ctx) => {
    const requestBody = await req.text();

    const urlSearchParams = new URLSearchParams(requestBody);

    const stringifyEntry = urlSearchParams.get('stringifyEntry');
    const entry = JSON.parse(stringifyEntry!);
    const favAction = urlSearchParams.get('favAction');

    return res(
      ctx.status(201),
      ctx.json({
        favAction,
        entry,
      })
    );
  }),
  rest.get('*/api/favorites', async (_, res, ctx) => {
    return res(ctx.status(200), ctx.json(mockFavorites));
  }),
  rest.get('*/api/client-feed', async (req, res, ctx) => {
    const url = new URL(req.url);

    const feedUrl = url.searchParams.get('feed-url');

    if (feedUrl === '/api/favorites') {
      return res(ctx.status(200), ctx.json(mockFavorites));
    }
    if (feedUrl === '/api/continue-watching') {
      return res(ctx.status(200), ctx.json(mockContinueWatching.feed));
    }

    if (feedUrl) {
      const parsedCollectionsRequest = parseCollectionsRequest(feedUrl);

      if (parsedCollectionsRequest?.kind === 'selector') {
        return res(
          ctx.status(200),
          ctx.json(buildSelectorFeed(parsedCollectionsRequest.itemId))
        );
      }

      if (
        parsedCollectionsRequest?.kind === 'editable' &&
        parsedCollectionsRequest.collectionId
      ) {
        return res(
          ctx.status(200),
          ctx.json(
            buildEditableCollectionFeed({
              itemId: parsedCollectionsRequest.itemId,
              collectionId: parsedCollectionsRequest.collectionId,
            })
          )
        );
      }
    }
  }),
  rest.post('*/api/client-feed', async (req, res, ctx) => {
    const url = new URL(req.url);
    const feedUrl = url.searchParams.get('feed-url');

    if (feedUrl !== '/mock/playlists/mutation') {
      return;
    }

    const requestBody = await req.json();
    applyMockPlaylistMutation(requestBody);

    return res(ctx.status(204));
  }),
  rest.get('*/api/continue-watching', async (_, res, ctx) => {
    return res(ctx.status(200), ctx.json(mockContinueWatching));
  }),
  rest.post('*/api/epg-programs', async (_, res, ctx) => {
    return res(ctx.status(200), ctx.json(replaceRemoteImageSrc(mockEpg)));
  })
);

export default worker;
